
import { TFunction } from "react-i18next";
import { EagerSVGIconMap } from "../../components/EagerSVG";

/**
 * A single navigation entry. When `items` is present the entry is treated
 * as a parent menu that toggles a submenu instead of resolving to `url`.
 */
export type NavItem = {
  name: string;
  type?: "link" | "submenu" | "quicklinks" // type of the item, defaults to "link"
  url?: string; // direct link (omit when the item only opens a submenu)
  items?: NavItem[]; // child links for sub‑menus
  icon?: EagerSVGIconMap; // icon name for eager SVG
}

export const notLoggedInNavigation = (t: TFunction): NavItem[] => [
  {
    name: t("navigation.dashboard"),
    type: "link",
    url: "/dashboard",
    icon: "FeedIcon",
  },
  {
    name: t("navigation.networkCoverage"),
    type: "link",
    url: "/marketplace",
    icon: "MarketplaceIcon",
  }
];

export const userNavigation = (t: TFunction): NavItem[] => [
  {
    name: t("navigation.map"),
    type: "link",
    url: "/map",
    icon: "MapIcon",
  },
  {
    name: t("navigation.networkAccess"),
    type: "submenu",
    icon: "NtripIcon",
    items: [
      { name: t("navigation.ntripAccounts"), url: "/accounts" },
      { name: t("navigation.subscriptions"), url: "/subscriptions" },
      { name: t("navigation.credits"), url: "/credits" },
    ],
  },
  {
    name: t("navigation.settings"),
    type: "submenu",
    icon: "SettingsIcon",
    items: [
      { name: t("navigation.billing"), url: "/settings/billing" },
      // Leaving this out since we're hiding Orgs for now
      // ...(branding.isRebranded ? [] : [{ name: t("navigation.manageOrganisations"), url: "/settings/manage-organisation-invites" }]),
    ],
  },

  {
    name: t("navigation.quickLinks"),
    type: "quicklinks",
    items: [
      { name: t("navigation.buySubscriptions"), url: "/subscriptions" },
      { name: t("navigation.buyCredits"), url: "/credits" },
      { name: t("navigation.createNtripAccounts"), url: "/accounts" },
      { name: t("navigation.viewNetworks"), url: "/marketplace" },
    ]
  }
]

export const adminNavigation = (t: TFunction): NavItem[] => [
  {
    name: t("navigation.networkManagement"),
    type: "submenu",
    url: "/admin/feed",
    icon: "NtripIcon",
    items: [
      { name: t("navigation.templates"), url: "/admin/networks" },
      { name: t("navigation.feed"), url: "/admin/feed" },
    ]
  },
  {
    name: t("navigation.networkAccess"),
    type: "submenu",
    icon: "MarketplaceIcon",
    items: [
      { name: t("navigation.ntripAccounts"), url: "/admin/accounts" },
      { name: t("navigation.subscriptions"), url: "/admin/subscriptions" },
    ],
  },
  {
    name: t("navigation.products"),
    type: "submenu",
    icon: "ProductIcon",
    items: [
      { name: t("navigation.automatedSubscriptions"), url: "/admin/products/billable-subscriptions" },
      { name: t("navigation.credits"), url: "/admin/products/credits" },
      { name: t("navigation.manualSubscriptions"), url: "/admin/products/non-billable-subscriptions" },
    ],
  },
  {
    name: t("navigation.map"),
    type: "link",
    url: "/admin/map",
    icon: "MapIcon",
  },
  {
    name: t("navigation.users"),
    type: "link",
    url: "/admin/users",
    icon: "UserIcon",
  },

  {
    name: t("navigation.settings"),
    type: "submenu",
    icon: "SettingsIcon",
    items: [
      { name: t("navigation.payments"), url: "/admin/settings/payments" },
      { name: t("navigation.accountManagement"), url: "/admin/settings/account-management" },
      { name: t("navigation.taxManagement"), url: "/admin/settings/tax-management" },
      { name: t("navigation.documents"), url: "/admin/settings/documents" },
      { name: t("navigation.invoices"), url: "/admin/settings/invoices" },
    ],
  },

  {
    name: t("navigation.quickLinks"),
    type: "quicklinks",
    items: [
      { name: t("navigation.createProducts"), url: "/admin/products/billable-subscriptions" },
      { name: t("navigation.createNtripAccounts"), url: "/admin/accounts" },
      { name: t("navigation.networkManagement"), url: "/admin/networks" },
    ]
  }
];
